import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import {
    SCHEDULE_APPLICATION_NON_CONFLICTING_STATUSES,
    SCHEDULE_APPLICATION_RETIRED_STATUSES,
    ScheduleApplicationStatus,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import { ScheduleSpecificDateApplyRequest } from "@components/accounting/schedules/manual/scheduleSpecificDateApply";
import { buildAccruedBilling } from "@components/accounting/schedules/scheduleApplyAccruedHelpers";
import { ScheduleCardValues } from "@components/accounting/schedules/scheduleCardCopy";
import { buildTransactionSpecificDateRequest } from "@components/transactions/views/transactionSchedulePayload";
import { MatchItemType, MatchableItem } from "../types";
import { findTransactionMatchId, parseInvoiceMatchId } from "../mappers";

const RETIRED_STATUSES = new Set<ScheduleApplicationStatus>(
    SCHEDULE_APPLICATION_RETIRED_STATUSES,
);
const NON_CONFLICTING_STATUSES = new Set<ScheduleApplicationStatus>(
    SCHEDULE_APPLICATION_NON_CONFLICTING_STATUSES,
);

export const MATCH_SCHEDULE_BLOCK_REASONS = {
    [MatchItemType.TRANSACTION]: "The matched invoice already has a schedule",
    [MatchItemType.INVOICE]: "The matched transaction already has a schedule",
} as const;

/** The transaction or invoice a match row stands for; null for bills and synthetic rows. */
export function parseMatchableItemSource(
    item: Pick<MatchableItem, "id" | "type">,
): { transactionId: number } | { invoiceId: string } | null {
    if (item.type === MatchItemType.TRANSACTION) {
        const transactionId = findTransactionMatchId(item.id);
        return transactionId === null ? null : { transactionId };
    }
    if (item.type === MatchItemType.INVOICE) {
        return { invoiceId: parseInvoiceMatchId(item.id) };
    }
    return null;
}

/** What the schedule modal needs to collect a start date for one match row. */
export function buildMatchItemSpecificDateRequest(options: {
    item: MatchableItem;
    template: { id: string; name: string };
}): ScheduleSpecificDateApplyRequest | null {
    const { item, template } = options;
    const source = parseMatchableItemSource(item);
    if (!source) {
        return null;
    }
    const label =
        item.description ?? item.bankDescription ?? item.documentNumber ?? "";
    if ("transactionId" in source) {
        return buildTransactionSpecificDateRequest({
            template,
            entityId: item.entityId,
            transactionId: source.transactionId,
            amount: item.amount,
            label,
            dateOnly: item.dateOnly,
        });
    }
    const amount = Math.abs(item.amount);
    const sourceDate = item.dateOnly ?? null;
    return {
        templateId: template.id,
        templateName: template.name,
        entityId: item.entityId,
        sourceType: ScheduleSourceType.invoice,
        amount,
        sourceLabel: label,
        sourceKey: item.id,
        sourceDate,
        billing: buildAccruedBilling({ sourceDate, amount, noun: "Invoice" }),
    };
}

function counterpartTypeOf(type: MatchItemType): MatchItemType | null {
    if (type === MatchItemType.TRANSACTION) {
        return MatchItemType.INVOICE;
    }
    if (type === MatchItemType.INVOICE) {
        return MatchItemType.TRANSACTION;
    }
    return null;
}

function isLive(application: ScheduleApplicationListItem): boolean {
    return !RETIRED_STATUSES.has(application.status);
}

/** The live schedule applied to the row's own transaction or invoice, if any. */
export function findMatchableItemApplication(options: {
    item: Pick<MatchableItem, "id" | "type">;
    applications: ScheduleApplicationListItem[];
}): ScheduleApplicationListItem | null {
    const source = parseMatchableItemSource(options.item);
    if (!source) {
        return null;
    }
    return (
        options.applications.find(
            (application) =>
                isLive(application) &&
                ("transactionId" in source
                    ? application.source.transactionId === source.transactionId
                    : application.source.invoiceId === source.invoiceId),
        ) ?? null
    );
}

export function toMatchScheduleCardValues(
    application: ScheduleApplicationListItem,
): ScheduleCardValues {
    const lastPeriod = application.periods.reduce<string | null>(
        (latest, period) =>
            latest === null || period.effectiveDate > latest
                ? period.effectiveDate
                : latest,
        null,
    );
    return {
        name: application.scheduleTemplateName,
        termEnd: lastPeriod,
        remainingAmount: application.remainingAmount,
    };
}

/**
 * Why a schedule cannot be applied to this row from the match drawer: the
 * other end of one of its links in the current selection is already
 * scheduled, so applying here would leave both ends scheduled. A suggested
 * draft on the other end is not a schedule yet and does not block.
 */
export function getScheduleApplyBlockReason(options: {
    item: Pick<MatchableItem, "id" | "type">;
    linkedItems: Array<Pick<MatchableItem, "id" | "type">>;
    applications: ScheduleApplicationListItem[];
}): string | null {
    const counterpartType = counterpartTypeOf(options.item.type);
    if (counterpartType === null) {
        return null;
    }
    const conflictingApplications = options.applications.filter(
        (application) => !NON_CONFLICTING_STATUSES.has(application.status),
    );
    const hasScheduledCounterpart = options.linkedItems.some(
        (linked) =>
            linked.type === counterpartType &&
            findMatchableItemApplication({
                item: linked,
                applications: conflictingApplications,
            }) !== null,
    );
    if (!hasScheduledCounterpart) {
        return null;
    }
    return options.item.type === MatchItemType.TRANSACTION
        ? MATCH_SCHEDULE_BLOCK_REASONS[MatchItemType.TRANSACTION]
        : MATCH_SCHEDULE_BLOCK_REASONS[MatchItemType.INVOICE];
}
